import {
  IBillingSchedulePeriodRespSchema,
  IBillingSchedulePeriodRespSchemaUI,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { createColumnHelper } from '../../../../components/Monetize/DataTableNew/appTable';
import { useMemo } from 'react';
import { MText } from '../../../../components/Monetize';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import { ProductLookupPopover } from '../../../../components/Monetize/LookupPopover/ProductLookupPopover';
import { MCurrency } from '../../../../components/Monetize/MCurrency';

interface InvoiceItemsTableProps {
  period: IBillingSchedulePeriodRespSchema | IBillingSchedulePeriodRespSchemaUI;
}

type InvoiceItem = IBillingSchedulePeriodRespSchema['items'][number] & {
  currency?: string;
  lineNumber?: number;
};

const columnHelper = createColumnHelper<InvoiceItem>();

const columns = [
  columnHelper.accessor('servicePeriod', {
    header: 'Service Period',
    size: 240,
    enableSorting: false,
    cell: ({ getValue }) => {
      const { start, end } = getValue();
      return (
        <MText>
          {toDateShort(start)} - {toDateShort(end)}
        </MText>
      );
    },
  }),
  columnHelper.accessor('offeringName', {
    header: 'Offering Name',
    size: 200,
    enableSorting: false,
  }),
  columnHelper.accessor('productName', {
    header: 'Product Name',
    size: 160,
    enableSorting: false,
    cell: ({ row }) => (
      <ProductLookupPopover
        productId={row.original.productId}
        label={row.original.productName}
      />
    ),
  }),
  columnHelper.accessor('quantity', {
    header: 'Quantity',
    size: 160,
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('pricePerUnit', {
    header: 'PPU',
    size: 160,
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <MCurrency
        options={{ currency: row.original.currency as string }}
        amount={getValue()}
      />
    ),
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    size: 160,
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <MCurrency
        options={{ currency: row.original.currency as string }}
        amount={getValue()}
      />
    ),
  }),
];

export const InvoiceItemsTable = ({
  period: { items },
}: InvoiceItemsTableProps) => {
  // Memoized so the `data` prop keeps a stable identity: an unstable array
  // re-runs the table's data preparation and selection pruning on every render
  // of the parent, and this table renders once per expanded row.
  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          ((a as InvoiceItem).lineNumber ?? 0) -
          ((b as InvoiceItem).lineNumber ?? 0),
      ),
    [items],
  );

  return (
    <DataTable
      columns={columns}
      data={sortedItems as InvoiceItem[]}
      clientControlled
      skipFilterValuePreprocessing
      containerProps={{ pl: 8, maxHeight: 'none' }}
      extraProps={{ containerProps: { overflowY: 'visible' } }}
      enablePagination={false}
    />
  );
};
